import config, { getApiUrl } from "@Config/index";
import { shortlistInventoryAsync } from "@Pages/Search/components/SearchListItem/components/ShortListButton/helper";
import { AppThunk } from "@Store/index";
import isInternalUser from "@Utils/isInternalUser";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import setLeadPersonalDetails from "helpers/setPersonalDetailsLocalstorage";
// import { toggleLoadZendeskAfterLogin } from "@Pages/PropertyPage/components/ZendeskChat/ChatBotReducer";
import { hasPartnerDashboardAccess } from "helpers/auth";
import { getFormattedSourceDetailsOnServer } from "@Utils/getFormattedSourceDetails";
import { sourceDetailsType } from "./appReducerType";
import { initWeChatBridge, weChatUserData } from "@Utils/clientUtils/weChatBridge/weChatBridge";
import { getUtmParams } from "@Pages/Home/components/PartnerReferSection/utils";
import { logger } from "helpers/logger";
import {
  extractLanguageFromPath,
  getLanguageFromRequest,
  getUserLanguageSSR,
  resolveUserCurrency,
} from "@Utils/languageCore";
import getTranslations from "helpers/getTolgeeTranslationKeys";
import { openToast } from "@Components/ToastNotification/toastReducer";
import getTogloeeLanguageCode from "helpers/getTogloeeLanguageCode";
import { getLanguageFileCode } from "helpers/getLanguageFileCode";
import {
  initializeKeyTracking,
  performClientSideComparison,
  initializeClientSideTracking,
  reportEmptyTranslationLoad,
  setReturnedTranslationKeys,
} from "helpers/trackUsedKeys";
import getCookie from "helpers/getCookie";
import { appShellFromRequest } from "constants/appShell";
import fetchCurrencyExchangeRates from "./data/fetchCurrencyExchangeRates";
import { getWhitelabelDefaultCurrency } from "@Utils/whitelabelLocalization";
// loadTranslationsSSR uses fs/path — must NOT be imported at module level
// or webpack will try to bundle it into the client. Use runtime require() in SSR middleware.
import { loadTranslationsCSR } from "helpers/loadTranslationsClient";
// Avoid importing from ChatBotReducer directly to prevent circular dependency
// (store → rootReducer → appReducer → ChatBotReducer → ZendeskChat → ZendeskChatIcon)
const toggleLoadZendeskAfterLogin = (payload: boolean) => ({
  type: "chatBotReducer/toggleLoadZendeskAfterLogin" as const,
  payload,
});
// import getUserLangForTolgee from "@Utils/getUserLangForTolgee";

interface AppData {
  metaObj: any;
  isUserLoggedIn: boolean;
  userData: any;
  isAuthTokenPresent: boolean;
  country: string;
  countryCode: string;
  showAppBanner: boolean;
  userLanguage: string;
  tolgeeTranslationKeys: Record<string, string>;
  cnToComRedirection: boolean;
  showSmartAppBanner: boolean;
  isUserDataFetched: boolean;
  searchViewedFlag: boolean;
  propertyViewedFlag: boolean;
  isDesktop: boolean;
  recentlyVisitedFlag: boolean;
  recentlyViewedFlag: boolean;
  locality: string;
  regionId: number;
  /** SEO (PLG-3406): search-page slugs on the current page that are noindexed. */
  noindexSlugs: string[];
  isTablet: boolean;
  isInternalUser: boolean;
  loadClarity: boolean;
  isMiniProgram: boolean;
  whiteListedCookies: any;
  configComponents?: Array<any>;
  thousandProperties?: Array<any>;
  hasPartnerDashboardAccess: boolean;
  nationality: string | null;
  providerId: string | null;
  inventoryId: string | null;
  UtmDetails: Partial<sourceDetailsType>;
  loginOneTapStatus: any;
  indexableDomain: boolean;
  showGridView: boolean;
  abTestVariants: Record<string, string>;
  isApp: boolean;
  /** New native shell only: `ios_native` | `android_native` | "". RN leaves this empty. */
  appShell: string;
  isUserDataFetching: boolean;
  nonce: string;
  lastScreen: string;
  trustPilotReviewCount?: number;
  trustPilotScore?: number;
  moduleName?: string;
  isSaleBannerActiveOnHomePage?: string;
  loadedRouteTags: string[]; // which route tags is loaded
  isLoadingTranslations: boolean; // Track if translations are being fetched
  isUsingS3Fallback: boolean; // Flag to indicate if S3 fallback is active if true. skip tolgeeClientMiddleware
  missingTranslationKeys: string[]; // Keys that are used but missing from Tolgee (for debugging)
  userCurrency?: string;
  currencyExchangeRates?: {
    baseCurrency: any;
    rates: Record<string, any>[];
  };
}

export const initialState: AppData = {
  metaObj: null,
  isUserLoggedIn: false,
  userData: null,
  isUserDataFetched: false,
  isUserDataFetching: false,
  country: "",
  showAppBanner: false,
  userLanguage: "en",
  tolgeeTranslationKeys: {},
  cnToComRedirection: false,
  showSmartAppBanner: true,
  isAuthTokenPresent: false,
  searchViewedFlag: false,
  propertyViewedFlag: false,
  isDesktop: false,
  recentlyVisitedFlag: false,
  recentlyViewedFlag: false,
  locality: "",
  regionId: -1,
  noindexSlugs: [],
  isTablet: false,
  isInternalUser: false,
  countryCode: "",
  loadClarity: false,
  isMiniProgram: false,
  whiteListedCookies: {},
  configComponents: [],
  thousandProperties: [],
  hasPartnerDashboardAccess: false,
  nationality: null,
  providerId: null,
  inventoryId: null,
  UtmDetails: {},
  loginOneTapStatus: {},
  indexableDomain: true,
  showGridView: false,
  abTestVariants: {},
  isApp: false,
  appShell: "",
  nonce: "",
  lastScreen: "APP_LAUNCHED",
  trustPilotReviewCount: 8300,
  trustPilotScore: 4.8,
  moduleName: undefined,
  isSaleBannerActiveOnHomePage: undefined,
  loadedRouteTags: [], // Initialize empty array for loaded route tags
  isLoadingTranslations: false, // Initialize as not loading
  isUsingS3Fallback: false, // Initialize as not using S3 fallback
  missingTranslationKeys: [], // Initialize empty array for missing keys
  userCurrency: "",
  currencyExchangeRates: { baseCurrency: "", rates: [] },
};

const appData = createSlice({
  name: "appData",
  initialState: {} as AppData,
  reducers: {
    getMetaSuccess: (state, { payload }: PayloadAction<string>) => {
      state.metaObj = payload;
    },
    /**
     * Patch meta.index without replacing the rest of metaObj (titles, canonicals, etc.).
     * Also clears a previous page's `robots` so CSR navigations don't leak e.g. pagination
     * `noindex,follow` onto a newly indexable route. Pages that need custom robots must
     * call getMetaSuccess after this seed (SSR: after ssr.tsx seed; CSR: in their own fetch).
     */
    setMetaIndex: (state, { payload }: PayloadAction<boolean>) => {
      const prev = state.metaObj && typeof state.metaObj === "object" ? { ...state.metaObj } : {};
      delete prev.robots;
      state.metaObj = { ...prev, index: payload };
    },
    UpdateIndexableDomain: (state, { payload }: PayloadAction<boolean>) => {
      state.indexableDomain = payload;
    },
    updateUserDataFetching: (state, { payload }: PayloadAction<any>) => {
      state.isUserDataFetching = payload;
    },
    updateUserData: (state, { payload }: PayloadAction<any>) => {
      state.userData = payload.data;
      state.isUserLoggedIn = payload.status;
      state.isUserDataFetched = true;
      state.isInternalUser = isInternalUser(payload.data);
      state.isUserDataFetching = false;
      setLeadPersonalDetails(payload.data);
    },
    updateAuthTokenStatus: (state, { payload }: PayloadAction<any>) => {
      state.isAuthTokenPresent = payload;
    },
    getCountrySuccess: (state, { payload }: PayloadAction<any>) => {
      state.country = payload;
    },
    updateShowAppBanner: (state, { payload }: PayloadAction<any>) => {
      state.showAppBanner = config.IS_CHINA_APP ? false : payload;
    },
    updateUserLanguage: (state, { payload }: PayloadAction<any>) => {
      // Values are language-specific; warm loadedRouteTags would skip refetch and leave
      // the previous language's map (or empty lookups → false missing alerts).
      if (state.userLanguage !== payload) {
        state.tolgeeTranslationKeys = {};
        state.loadedRouteTags = [];
      }
      state.userLanguage = payload;
    },
    updateTolgeeKeys: (state, { payload }: PayloadAction<Record<string, string>>) => {
      state.tolgeeTranslationKeys = {
        ...state.tolgeeTranslationKeys,
        ...payload,
      };
    },
    mergeTolgeeKeys: (
      state,
      { payload }: PayloadAction<{ keys: Record<string, string>; routeTags: string[] }>,
    ) => {
      state.tolgeeTranslationKeys = {
        ...state.tolgeeTranslationKeys,
        ...payload.keys,
      };
      if (!state.loadedRouteTags) {
        state.loadedRouteTags = [];
      }
      const newTags = payload.routeTags.filter((tag) => !state.loadedRouteTags.includes(tag));
      state.loadedRouteTags = [...state.loadedRouteTags, ...newTags];
    },
    clearTolgeeKeys: (state) => {
      state.tolgeeTranslationKeys = {};
      state.loadedRouteTags = [];
    },
    setIsLoadingTranslations: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoadingTranslations = payload;
    },
    setIsUsingS3Fallback: (state, { payload }: PayloadAction<boolean>) => {
      state.isUsingS3Fallback = payload;
    },
    setMissingTranslationKeys: (state, { payload }: PayloadAction<string[]>) => {
      state.missingTranslationKeys = payload;
    },
    updatecnToComRedirection: (state, { payload }: PayloadAction<any>) => {
      state.cnToComRedirection = payload;
    },
    updateShowSmartAppBanner: (state, { payload }: PayloadAction<any>) => {
      state.showSmartAppBanner = config.IS_CHINA_APP ? false : payload;
    },
    updateSearchViewedFlag: (state, { payload }: PayloadAction<any>) => {
      state.searchViewedFlag = payload;
    },
    updatePropertyViewedFlag: (state, { payload }: PayloadAction<any>) => {
      state.propertyViewedFlag = payload;
    },
    updateIsDesktop: (state, { payload }: PayloadAction<any>) => {
      state.isDesktop = payload;
    },
    updateIsMiniProgram: (state, { payload }: PayloadAction<any>) => {
      state.isMiniProgram = payload;
    },
    updateRecentlyViewedFlag: (state, { payload }: PayloadAction<any>) => {
      state.recentlyViewedFlag = payload;
    },
    updateLocality: (state, { payload }: PayloadAction<any>) => {
      state.locality = payload.locality;
    },
    updateRegionId: (state, { payload }: PayloadAction<any>) => {
      state.regionId = payload.regionId;
    },
    updateNoindexSlugs: (state, { payload }: PayloadAction<string[]>) => {
      state.noindexSlugs = payload || [];
    },
    updateCountryCode: (state, { payload }: PayloadAction<any>) => {
      state.countryCode = payload.countryCode;
    },
    updateIsTablet: (state, { payload }: PayloadAction<any>) => {
      state.isTablet = payload;
    },
    updateNonce: (state, { payload }: PayloadAction<any>) => {
      state.nonce = payload;
    },
    updateModuleName: (state, { payload }: PayloadAction<string | undefined>) => {
      state.moduleName = payload;
    },
    updateLoadClarity: (state, { payload }: PayloadAction<any>) => {
      state.loadClarity = payload;
    },
    updateWhitelistedCookie: (state, { payload }: PayloadAction<any>) => {
      state.whiteListedCookies = {
        ...state.whiteListedCookies,
        ...payload,
      };
    },
    updateIsApp: (state, { payload }: PayloadAction<any>) => {
      state.isApp = payload;
    },
    updateAppShell: (state, { payload }: PayloadAction<string>) => {
      state.appShell = payload;
    },
    addConfigurableComponents: (state, { payload }: PayloadAction<any>) => {
      state.configComponents = [...(state.configComponents || []), ...payload];
    },
    addThousandProperties: (state, { payload }: PayloadAction<any>) => {
      state.thousandProperties = payload;
    },
    updateHasPartnerDashboardAccess: (state, { payload }: PayloadAction<any>) => {
      state.hasPartnerDashboardAccess = payload;
    },
    updateNationality: (state, { payload }: PayloadAction<any>) => {
      state.nationality = payload?.nationality;
    },
    updateProviderId: (state, { payload }: PayloadAction<any>) => {
      state.providerId = payload?.providerId;
    },
    updateInventoryId: (state, { payload }: PayloadAction<any>) => {
      state.inventoryId = payload?.inventoryId;
    },
    UpdateUtmDetails: (state, { payload }: PayloadAction<any>) => {
      state.UtmDetails = payload;
    },
    updateLoginOpenTapStatus: (state, { payload }: PayloadAction<any>) => {
      state.loginOneTapStatus = payload;
    },
    updateUserNameAndProfileImage: (
      state,
      { payload }: PayloadAction<{ name: string; profile_image_url: string }>,
    ) => {
      if (state.userData?.data) {
        state.userData.data = {
          ...state.userData.data,
          name: payload.name,
          profile_image_url: payload.profile_image_url,
        };
      }
    },
    updateShowGridView: (state, { payload }: PayloadAction<any>) => {
      state.showGridView = payload;
    },
    updateAbTestVariants: (state, { payload }: PayloadAction<Record<string, string>>) => {
      state.abTestVariants = { ...state.abTestVariants, ...payload };
    },
    updateLastScreen: (state, { payload }: PayloadAction<string>) => {
      state.lastScreen = payload;
    },
    updateTrustPilotReviewCount: (state, { payload }: PayloadAction<number>) => {
      state.trustPilotReviewCount = payload;
    },
    updateTrustPilotScore: (state, { payload }: PayloadAction<number>) => {
      state.trustPilotScore = payload;
    },
    updateIsSaleBannerActiveOnHomePage: (state, { payload }: PayloadAction<string | undefined>) => {
      state.isSaleBannerActiveOnHomePage = payload;
    },
    logoutSuccess: (state) => {
      state.isUserLoggedIn = false;
      state.userData = null;
      state.isAuthTokenPresent = false;
      state.isUserDataFetched = false;
      state.isInternalUser = false;
    },
    updateUserCurrency: (state, { payload }) => {
      state.userCurrency = payload.userCurrency;
    },
    updateCurrencyExchangeRates: (state, { payload }) => {
      state.currencyExchangeRates = {
        baseCurrency: state.userCurrency,
        rates: payload,
      };
    },
  },
});

export default appData.reducer;
export const {
  getMetaSuccess,
  setMetaIndex,
  updateUserData,
  updateUserDataFetching,
  getCountrySuccess,
  updateShowAppBanner,
  updateShowSmartAppBanner,
  updateAuthTokenStatus,
  updateSearchViewedFlag,
  updatePropertyViewedFlag,
  updateIsDesktop,
  updateRecentlyViewedFlag,
  updateLocality,
  updateRegionId,
  updateNoindexSlugs,
  updateCountryCode,
  updateIsTablet,
  updateLoadClarity,
  updateIsMiniProgram,
  updateWhitelistedCookie,
  addConfigurableComponents,
  updateHasPartnerDashboardAccess,
  updateNationality,
  updateProviderId,
  updateInventoryId,
  UpdateUtmDetails,
  updateLoginOpenTapStatus,
  updateUserNameAndProfileImage,
  UpdateIndexableDomain,
  updateShowGridView,
  updateAbTestVariants,
  updateUserLanguage,
  updatecnToComRedirection,
  updateNonce,
  updateModuleName,
  addThousandProperties,
  updateIsApp,
  updateAppShell,
  updateLastScreen,
  updateTrustPilotReviewCount,
  updateTrustPilotScore,
  updateIsSaleBannerActiveOnHomePage,
  logoutSuccess,
  updateTolgeeKeys,
  mergeTolgeeKeys,
  clearTolgeeKeys,
  setIsLoadingTranslations,
  setIsUsingS3Fallback,
  setMissingTranslationKeys,
  updateUserCurrency,
  updateCurrencyExchangeRates,
} = appData.actions;

// only for server
export const authMiddlewareServer =
  ({ req, staticContext, isDesktop }: any): AppThunk =>
  async (dispatch) => {
    let weChatToken = "";
    if (config.IS_CHINA_APP) {
      weChatToken = initWeChatBridge(req, staticContext);

      const miniProgramUserData = weChatUserData(req);
      dispatch(
        updateUserNameAndProfileImage({
          name: miniProgramUserData?.userNickName,
          profile_image_url: miniProgramUserData?.userAvatarUrl,
        }),
      );
    }

    const token = req.cookies.user_auth_token || weChatToken || null;

    // Get authentication status
    let authStatus = { status: false, data: {} };

    if (!token) {
      dispatch(updateUserData(authStatus));
      if (req?.cookies?.is_internal_user) {
        staticContext.cookie = {
          key: "is_internal_user",
          value: "",
          extra: { maxAge: 0 },
        };
      }
      return;
    }

    if (token && req.query?.load_zendesk) {
      dispatch(toggleLoadZendeskAfterLogin(true));
    }

    if (token && req.query.shortlist_inv) {
      try {
        await shortlistInventoryAsync(req.query.shortlist_inv, token);
      } catch (error) {
        console.log("Error shortlisting inventory:", error);
      }
    }

    try {
      const res = await axios(`${getApiUrl(true)}/api/v0/users/details`, {
        params: {
          user_auth_token: token,
        },
      });
      authStatus = {
        status: Boolean(res?.status === 200),
        data: res.data ?? {},
      };
      if (isDesktop && req?.path !== "/") {
        if (isInternalUser(res.data)) {
          if (!req?.cookies?.is_internal_user) {
            staticContext.url = req.url;
            staticContext.cookie = {
              key: "is_internal_user",
              value: "true",
              extra: { maxAge: 365 * 24 * 60 * 60 * 1000 },
            };
          }
        } else {
          staticContext.cookie = {
            key: "is_internal_user",
            value: "",
            extra: { maxAge: 0 },
          };
        }
      }

      // ⚠️  THIS RESOLVES A FLAG. IT USED TO BE A REDIRECT, AND THAT WAS THE BUG.
      //
      // A partner asking for /list was answered with `staticContext.url = "/connect"`,
      // which ssr.tsx turns into a 301/302 — so a partner never saw the page, including
      // its own "Go to Dashboard" CTA. That CTA reads the flag dispatched below, so the
      // one code path that knew you were a partner was also the path that made the
      // knowledge unusable. Sending someone away from a page that carries a button to
      // exactly where they were being sent is the wrong shape.
      //
      // Now /list renders for everyone and a partner is OFFERED the dashboard rather
      // than moved to it — `Header` and the hero both switch their CTA on this flag.
      //
      // ⚠️  STILL EXACTLY `/list`, so the flag is not resolved for the localised routes
      // (`/:lang(es|de|fr)/list`) or for client-side navigation into the page, where no
      // SSR request happens at all. A partner arriving either way sees the non-partner
      // CTA. See the note in ListWithUsV2/components/Header/Header.tsx.
      if (res?.status === 200 && req?.path === "/list") {
        const dashboardAccess = await hasPartnerDashboardAccess(
          token,
          "api/v0/auth/providers/users",
        );
        dispatch(updateHasPartnerDashboardAccess(dashboardAccess));
      }

      dispatch(updateUserData(authStatus));
    } catch (error) {
      if (isDesktop) {
        staticContext.cookie = {
          key: "is_internal_user",
          value: "",
          extra: { maxAge: 0 },
        };
      }
      staticContext.cookie = {
        key: "user_auth_token",
        value: "",
        extra: { maxAge: 0 },
      };
      dispatch(updateUserData(authStatus));
    }
  };

export const authMiddlewareClient =
  (addLoading = false): AppThunk =>
  async (dispatch) => {
    // Get authentication status
    let authStatus = { status: false, data: {} };
    try {
      if (addLoading) {
        dispatch(updateUserDataFetching(true));
      }
      const shortlistQuery = new URLSearchParams(window?.location?.search || "").get(
        "shortlist_inv",
      );
      const user_auth_token = window.localStorage?.getItem?.("user_auth_token") || null;
      if (shortlistQuery) {
        await shortlistInventoryAsync(shortlistQuery, user_auth_token);
      }
      const res = await axios(`${config.API_URL}/api/v0/users/details`, {
        withCredentials: true,
        params: {
          user_auth_token,
        },
      });
      authStatus = { status: Boolean(res?.status === 200), data: res.data ?? {} };
      dispatch(updateUserData(authStatus));
    } catch (error) {
      dispatch(
        openToast({
          title: "Something Went Wrong!",
          message: "Please try again",
        }),
      );
      if (typeof window !== undefined && typeof window.Sentry !== "undefined") {
        window.Sentry.captureException(`Auth Middleware Client Error -> ${JSON.stringify(error)}`);
      }
      window.localStorage?.removeItem("user_auth_token");
      dispatch(updateUserData(authStatus));
    }
  };

export const getAuthTokenStatus =
  ({ req }: any): AppThunk =>
  async (dispatch) => {
    const token = req.cookies.user_auth_token || null;
    dispatch(updateAuthTokenStatus(!!token));
  };

export const appBannerMiddleware =
  ({ req }: any): AppThunk =>
  async (dispatch) => {
    const showAppBanner = req.cookies.showAppBanner || null;
    const initialUtmParams = req.cookies.initial_utm_params || null;
    const reqParams = new URLSearchParams(req.query);
    const { utmSource, utmMedium, campaign } = getUtmParams(reqParams, initialUtmParams);
    const isPartnerReferred = campaign === "partner" && utmSource && utmMedium;
    let shouldShowAppBanner: boolean;

    if (isPartnerReferred) {
      shouldShowAppBanner = false;
    } else if (req.headers["is-app"]) {
      shouldShowAppBanner = req.headers["is-app"] !== "true";
    } else if (req?.cookies?.is_app) {
      shouldShowAppBanner = req.cookies.is_app !== "true";
    } else {
      shouldShowAppBanner = showAppBanner !== "false";
    }

    dispatch(updateShowAppBanner(shouldShowAppBanner));
  };

export const chinaLanguageModalMiddleWare =
  ({ req }: any): AppThunk =>
  async (dispatch, getState) => {
    const pathname = req.path || req.url || "";
    const currentLanguage = getState().appData?.userLanguage;

    if (extractLanguageFromPath(pathname) === "en") {
      dispatch(updatecnToComRedirection(true));
    }

    if (!currentLanguage || currentLanguage === "en") {
      const userLang = getLanguageFromRequest(req);

      if (userLang && userLang !== currentLanguage) {
        dispatch(updateUserLanguage(userLang));
      }
    }
  };

export const tolgeeMiddleWare =
  ({ req, route }: any): AppThunk =>
  async (dispatch, getState) => {
    if (!config.ENABLE_TOLGEE) return;

    // Use userLanguage from Redux state (set by chinaLanguageModalMiddleWare)
    // Fall back to getUserLanguageSSR if state doesn't have it yet
    const state = getState();
    const internalLang = state?.appData?.userLanguage || getUserLanguageSSR(req);
    const userLang = getLanguageFileCode(internalLang);
    const routeTags = route?.tagforkey || route?.tag || [];
    const pageName = route?.path || req.path || "unknown";

    initializeKeyTracking(routeTags, userLang, pageName, "ssr");

    try {
      const result = await getTranslations(userLang, routeTags, false);
      console.info("Server-side translations received:", {
        keyCount: Object.keys(result.data || {}).length,
        usedS3: result.usedS3,
        language: userLang,
      });

      if (__SERVER__) {
        setReturnedTranslationKeys(result.data || {});
      }

      dispatch(mergeTolgeeKeys({ keys: result.data, routeTags }));
    } catch (error) {
      logger.error("Error loading translation keys:", error);
      const result = await getTranslations(userLang, routeTags, false);

      if (__SERVER__) {
        setReturnedTranslationKeys(result.data || {});
      }

      dispatch(updateTolgeeKeys(result.data));
    }
  };

/** True when userLanguage changed while a CSR translation fetch was in flight. */
const isStaleClientTranslationRequest = (
  getState: () => { appData: { userLanguage?: string } },
  requestedLang: string,
): boolean => {
  const currentLang = getState().appData.userLanguage || "en";
  return currentLang !== (requestedLang || "en");
};

export const tolgeeClientMiddleware =
  ({ lang, tags }: { lang: string; tags: string[] }): AppThunk =>
  async (dispatch, getState) => {
    if (!config.ENABLE_TOLGEE) return;

    if (!tags?.length) {
      return;
    }

    const state = getState();
    const { isUsingS3Fallback, tolgeeTranslationKeys } = state.appData;

    // if S3 fallback is active, skip all
    if (isUsingS3Fallback) {
      return;
    }

    const { loadedRouteTags = [] } = state.appData;
    const newTags = tags.filter((tag: string) => !loadedRouteTags.includes(tag));
    const tolgeeLanguage = getTogloeeLanguageCode(lang);
    const currentPath = typeof window !== "undefined" ? window.location.pathname : "unknown";

    // Always track with full route tags (caller should pass full tagforkey).
    if (typeof window !== "undefined") {
      initializeClientSideTracking(tags, tolgeeLanguage, currentPath);
      (window as any).__reduxDispatch = dispatch;
    }

    const scheduleCompare = () => {
      if (typeof window === "undefined") return;
      performClientSideComparison(
        () => getState().appData.tolgeeTranslationKeys || {},
        () => getState().appData.isLoadingTranslations,
      );
    };

    if (newTags.length === 0) {
      scheduleCompare();
      return;
    }

    try {
      dispatch(setIsLoadingTranslations(true));

      const hasExistingKeys = Object.keys(tolgeeTranslationKeys || {}).length > 0;
      const result = await getTranslations(tolgeeLanguage, newTags, hasExistingKeys);

      // Language switched mid-flight — do not restore the previous locale's map.
      if (isStaleClientTranslationRequest(getState, lang)) {
        return;
      }

      if (result.usedS3) {
        dispatch(setIsUsingS3Fallback(true));
      }

      dispatch(mergeTolgeeKeys({ keys: result.data, routeTags: newTags }));
      scheduleCompare();
    } catch (error) {
      if (isStaleClientTranslationRequest(getState, lang)) {
        return;
      }
      logger.error("Error in tolgeeClientMiddleware:", error);

      dispatch(mergeTolgeeKeys({ keys: {}, routeTags: [] }));
      console.warn("All endpoints failed - using empty translations");
    } finally {
      // Always clear — a newer in-flight request re-sets true; skipping here can leave
      // loading stuck if the newer request already finished or never set loading.
      dispatch(setIsLoadingTranslations(false));
    }
  };

// ─── NEW STATIC TRANSLATION MIDDLEWARE (replaces tolgeeMiddleWare) ─────────

export const translationMiddleWare =
  ({ req, route }: any): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const userLang = state?.appData?.userLanguage || getUserLanguageSSR(req);
    const routeTags = route?.tagforkey || route?.tag || [];
    const pageName = route?.path || req?.path || "unknown";
    const trackingLang = getTogloeeLanguageCode(userLang);

    initializeKeyTracking(routeTags, trackingLang, pageName, "ssr");

    if (routeTags.length > 0) {
      dispatch(setIsLoadingTranslations(true));
    }

    try {
      // Dynamic require guarded by __SERVER__ — rspack dead-code-eliminates
      // this entire block in the client bundle since __SERVER__ = false.
      // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
      const { loadTranslationsSSR } = __SERVER__
        ? require("helpers/loadTranslations")
        : { loadTranslationsSSR: () => Promise.resolve({}) };
      const translations = await loadTranslationsSSR(userLang, routeTags);
      const keyCount = Object.keys(translations).length;

      console.info("Static translations loaded:", {
        keyCount,
        language: userLang,
        tags: routeTags,
      });

      if (__SERVER__) {
        setReturnedTranslationKeys(translations);
      }

      if (routeTags.length > 0 && keyCount === 0) {
        reportEmptyTranslationLoad({
          language: trackingLang,
          routeTags,
          pageName,
          source: "ssr",
        });
      }

      // Only mark tags loaded when keys were returned; empty success keeps tags retryable.
      dispatch(mergeTolgeeKeys({ keys: translations, routeTags: keyCount === 0 ? [] : routeTags }));
    } catch (error) {
      logger.error("Error loading static translations:", error);
      if (__SERVER__) {
        setReturnedTranslationKeys({});
      }
      if (routeTags.length > 0) {
        reportEmptyTranslationLoad({
          language: trackingLang,
          routeTags,
          pageName,
          source: "ssr",
        });
      }
      // Do NOT mark routeTags as loaded — empty merge would permanently skip refetch.
      dispatch(mergeTolgeeKeys({ keys: {}, routeTags: [] }));
    } finally {
      dispatch(setIsLoadingTranslations(false));
    }
  };

// ─── NEW STATIC TRANSLATION CLIENT MIDDLEWARE (replaces tolgeeClientMiddleware) ─

/**
 * Ensure route translation tags are loaded and schedule a single CSR compare.
 * Pass the FULL route `tagforkey` as `tags` (not only missing tags) so tracker
 * metadata matches the page. Fetch still only requests tags not yet in loadedRouteTags.
 */
export const translationClientMiddleware =
  ({ lang, tags }: { lang?: string; tags?: string[] }): AppThunk =>
  async (dispatch, getState) => {
    // rootData may invoke this with SSR props ({ req, route }) — no-op without tags.
    if (!tags?.length || !lang) return;

    const state = getState();
    const { loadedRouteTags = [] } = state.appData;
    const newTags = tags.filter((tag: string) => !loadedRouteTags.includes(tag));

    const pageName = typeof window !== "undefined" ? window.location.pathname : "unknown";
    const trackingLang = getTogloeeLanguageCode(lang);

    // Always track with full route tags (even on warm cache).
    if (typeof window !== "undefined") {
      initializeClientSideTracking(tags, trackingLang, pageName);
      (window as any).__reduxDispatch = dispatch;
    }

    const scheduleCompare = () => {
      if (typeof window === "undefined") return;
      performClientSideComparison(
        () => getState().appData.tolgeeTranslationKeys || {},
        () => getState().appData.isLoadingTranslations,
      );
    };

    // Warm cache: nothing to fetch, but still compare after render settles.
    if (newTags.length === 0) {
      scheduleCompare();
      return;
    }

    try {
      dispatch(setIsLoadingTranslations(true));
      const translations = await loadTranslationsCSR(lang, newTags);

      // Language switched mid-flight — do not restore the previous locale's map.
      if (isStaleClientTranslationRequest(getState, lang)) {
        return;
      }

      const keyCount = Object.keys(translations).length;

      if (keyCount === 0) {
        reportEmptyTranslationLoad({
          language: trackingLang,
          routeTags: newTags,
          pageName,
          source: "csr",
        });
      }

      dispatch(mergeTolgeeKeys({ keys: translations, routeTags: keyCount === 0 ? [] : newTags }));
      scheduleCompare();
    } catch (error) {
      if (isStaleClientTranslationRequest(getState, lang)) {
        return;
      }
      logger.error("Error in translationClientMiddleware:", error);
      reportEmptyTranslationLoad({
        language: trackingLang,
        routeTags: newTags,
        pageName,
        source: "csr",
      });
      // Keep tags unloaded so the next navigation/effect can retry.
      dispatch(mergeTolgeeKeys({ keys: {}, routeTags: [] }));
    } finally {
      // Always clear — a newer in-flight request re-sets true; skipping here can leave
      // loading stuck if the newer request already finished or never set loading.
      dispatch(setIsLoadingTranslations(false));
    }
  };

// ─────────────────────────────────────────────────────────────────────────────

export const gridViewMiddleware =
  ({ req }: any): AppThunk =>
  async (dispatch) => {
    const showGridView = req.cookies.search_grid_view === "true";
    dispatch(updateShowGridView(showGridView));
  };

export const dispatchCookies =
  ({ req }: any): AppThunk =>
  async (dispatch, getState) => {
    const whitelistedCookies = ["user_flow_token", "searchRegionId"].reduce((acc: any, it) => {
      if (req?.cookies?.[it]) {
        acc[it] = req?.cookies?.[it];
      }
      return acc;
    }, {});

    // The visitor's own currency wins; a whitelabel partner's default fills in when they have
    // none (whitelabel config is prefetched before rootData runs).
    const requestCurrency =
      resolveUserCurrency(req) || getWhitelabelDefaultCurrency(getState()?.whitelabelData?.config);
    if (requestCurrency) {
      dispatch(updateUserCurrency({ userCurrency: requestCurrency }));
    }

    dispatch(updateWhitelistedCookie(whitelistedCookies));
    dispatch(UpdateUtmDetails(getFormattedSourceDetailsOnServer(req.cookies, req.query)));
  };

export const smartBannerMiddleware =
  ({ req }: any): AppThunk =>
  async (dispatch) => {
    const showSmartAppBanner = req.cookies.showSmartAppBanner || null;
    dispatch(updateShowSmartAppBanner(showSmartAppBanner !== "false"));
  };

// eslint-disable-next-line consistent-return
export const fetchConfigComponents = async ({ regionId = 0, page = "" }: any) => {
  try {
    const res = await axios.get(
      `${config.API_URL}/api/v0/application_config?${
        page ? `page=${page}` : `region_id=${regionId}`
      }`,
      {
        withCredentials: true,
      },
    );
    return res?.data?.data?.result || [];
  } catch (error: any) {
    logger.error(error);
  }
};
export const fetchThousandProperties = async (params = {}) => {
  try {
    const url = `${config.API_URL}/api/v0/inventories/fetch_thousand_properties`;
    const response = await axios.get(url, { params });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching thousand properties:", error);
    throw error;
  }
};

export const dispatchMetaIndexTag =
  ({ req }: any): AppThunk =>
  async (dispatch) => {
    const { hostname } = req;
    // const pathname = req.path || req.url || "";

    // Check if path contains language route pattern and if it's not 'en' or 'zh'
    // const languageRoutePattern = /^\/(en|fr|es|de|zh)\//;
    // const match = pathname.match(languageRoutePattern);
    // const hasNonEnZhLanguageRoute = match ? !["en", "zh"].includes(match[1]) : false;  // removed on demand making language routes indexable
    const indexableDomain = !hostname.includes("hk.");
    dispatch(UpdateIndexableDomain(indexableDomain));
  };

export const abTestMiddleware =
  ({ req }: any): AppThunk =>
  async (dispatch) => {
    // List all AB test cookie names here
    const abTestCookieNames = [
      "search_page_v2",
      "search_sorting_key_v2",
      "search_sorting_key_v2_50",
      "scholarship_variant",
    ];
    const scholarShipVarientFromHeader =
      req.headers["x-feature-scholarship_variant"] || req.headers["X-Feature-Scholarship_variant"];
    const abTestVariants: Record<string, string> = {};
    abTestCookieNames.forEach((name) => {
      if (req.cookies[name]) {
        abTestVariants[name] = req.cookies[name];
      }
    });

    if (!__CLIENT__) {
      abTestVariants["scholarship_variant"] = scholarShipVarientFromHeader;
    }

    dispatch(updateAbTestVariants(abTestVariants));
  };

export const appMiddleware =
  ({ req, isDesktop: isDesktopFromReq }: any): AppThunk =>
  async (dispatch, getState) => {
    if (!req) return;
    // rootData already passes isDesktop: !isMobile. Use that — do not read
    // Redux, which is still the initial false until updateIsDesktop runs.
    const isDesktop =
      typeof isDesktopFromReq === "boolean"
        ? isDesktopFromReq
        : Boolean(getState().appData.isDesktop);
    const isApp =
      !isDesktop &&
      (req.headers["is-app"] === "true" ||
        req?.cookies?.is_app === "true" ||
        req?.query?.is_app === "true");
    dispatch(updateIsApp(isApp));
    dispatch(updateAppShell(appShellFromRequest(req)));
  };

export const userCurrencyExchangeMiddleware =
  ({ req }: any): AppThunk =>
  async (dispatch, getState) => {
    // Header first, cookie second. The app sends `app-currency` on the request it issues,
    // because a cookie written just before that request is not reliably visible to it — the
    // first load after a currency change could otherwise render in the previous currency while
    // a refresh of the same page rendered correctly.
    const userCurrency =
      (__CLIENT__ ? getCookie("user_currency") || "" : resolveUserCurrency(req)) ||
      getWhitelabelDefaultCurrency(getState()?.whitelabelData?.config);

    const existingBaseCurrency = getState()?.appData?.currencyExchangeRates?.baseCurrency || "";

    if (!userCurrency || userCurrency === existingBaseCurrency) return;

    try {
      const rates = await fetchCurrencyExchangeRates({ base: userCurrency });
      dispatch(updateCurrencyExchangeRates(rates));
    } catch (e) {
      console.error("Failed to fetch currency exchange rates", e);
    }
  };
